import { useState, useEffect, useMemo, useRef } from 'react'
import Formsy from 'formsy-react'
// eslint-disable-next-line no-unused-vars
import { Autocomplete,  TextField,  Button,  IconButton,  Table,  TableContainer,  TableHead,  
        TableBody,  TableSortLabel, Paper, DialogTitle,  DialogContent, DialogActions, 
      } from '@mui/material'
import { Add, Delete, Edit } from '@mui/icons-material'

import { D, Loading, SearchField, TRow, TCell, TextFieldFormsy, ConfirmDelete } from '../include'

const ICON_CELL_WIDTH = 48; // largura confortável para ícones

// ==========================================
// COMPONENTE: Editar/adicionar embarcaçoes
// ==========================================
const EditEmbarcacao = ({ edit, row, api, alert, dialogClose, refreshTable }) => {
  const initialNome = row?.nome || '';
  const initialGrupoNome = row?.grupos?.[0]?.nome || '';
  const initialGrupoID = row?.grupos?.[0]?.id || null;

  const [nome, setNome] = useState(initialNome);
  const [grupo, setGrupo] = useState(initialGrupoNome || null);
  const [grupoID, setGrupoID] = useState(initialGrupoID);
  
  const [grupos, setGrupos] = useState([]);
  const [gruposName, setGruposName] = useState([]);

  useEffect(() => {
    api.get('/Grupos').then(res => {
      const gruposArray = Array.isArray(res) ? res : [];
      const nomes = gruposArray.map(g => g.nome);

      setGrupos(gruposArray);
      setGruposName(nomes);
      
      // Valida se o grupo inicial ainda existe na lista
      if (initialGrupoNome && !nomes.includes(initialGrupoNome)) {
        setGrupo(null);
        setGrupoID(null);
      }
    }).catch(e => console.error("Erro ao carregar Grupos", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O canSave não precisa ser um estado, ele é derivado diretamente das variáveis atuais!
  const canSave = !!(nome.trim() && grupoID);

  const handleSave = () => {
    const payload = { nome: nome.trim(), grupo: grupoID };
    dialogClose();

    if (!edit) {
      api.post('/embarcacoes', payload)
        .then(r => {
          alert(r.msg);
          refreshTable();
        })
        .catch(e => alert(e.msg || 'Erro ao criar embarcação'));
    } else {
      payload.id = row.id;
      api.put('/embarcacoes', payload)
        .then(r => {
          alert(r.msg);
          refreshTable();
        })
        .catch(e => alert(e.msg || 'Erro ao atualizar embarcação'));
    }
  };

  return (
    <Formsy onValidSubmit={handleSave}>
      <DialogTitle style={{ minWidth: '420px' }}>
        {!edit ? 'Adicionar Embarcação' : 'Editar Embarcação'}
      </DialogTitle>
      
      <DialogContent>
        <D h="1rem" />
        <TextFieldFormsy
          value={nome}
          onChange={v => setNome(v)}
          type="text"
          name="nome"
          label="Nome"
          variant="outlined"
          required
        />

        {/* SINGLE SELECT de Grupo (obrigatório) */}
        <Autocomplete
          options={gruposName}
          value={grupo}
          isOptionEqualToValue={(option, value) => option === value}
          renderInput={params => <TextField {...params} label="Grupo" required />}
          style={{ marginTop: '1rem', width: '100%' }}
          onChange={(_, value) => {
            const g = grupos.find(x => x.nome === value);
            setGrupo(value || '');
            setGrupoID(g ? g.id : null);
          }}
        />
        <D h="2rem" />
      </DialogContent>
      
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>
          Cancelar
        </Button>
        <Button variant="outlined" color="primary" onClick={handleSave} disabled={!canSave}>
          Salvar
        </Button>
      </DialogActions>
    </Formsy>
  );
};



// ==========================================
// COMPONENTE PRINCIPAL: Lista de Embarcacoes cadastradas
// ==========================================
export function Embarcacoes({ api, alert, dialog, dialogClose, permission }) {
  const [searchField, setSearchField] = useState('');
  const [dataTable, setDataTable] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 1. Cria a referência inicial
  const isFirstRender = useRef(true);
  
  const [orderBy, setOrderBy] = useState('nome');
  const [orderDir, setOrderDir] = useState('asc');

  const updateData = () => {
    setIsLoading(true);
    api.get('/embarcacoes')
      .then(res => {
        setDataTable(Array.isArray(res) ? res : []);
        setIsLoading(false);
      })
      .catch(e => {
        alert("Erro ao buscar dados.");
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      updateData();
      isFirstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (col) => {
    const isAsc = orderBy === col && orderDir === 'asc';
    setOrderDir(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
  };

  const openEditDialog = (row = null, isEdit = false) => {
    dialog(
      <EditEmbarcacao
        edit={isEdit}
        row={row}
        api={api}
        alert={alert}
        dialogClose={dialogClose}
        refreshTable={updateData}
      />
    );
  };

  const openDeleteDialog = (row) => {
    dialog(
      <ConfirmDelete
        msg="Tem certeza que deseja excluir a embarcação:"
        itemName={row.nome}
        onCancel={dialogClose}
        onConfirm={() => {
          api.delete('/embarcacoes', { data: { id: row.id }})
            .then(r => {
              alert(r.msg);
              updateData(); 
              dialogClose();
            })
            .catch(e => alert(e.msg || 'Erro ao excluir a embarcação.'));
        }}
      />
    );
  };

  // Memoização dupla: Filtro e Ordenação aplicados em memória de forma limpa
  const processedData = useMemo(() => {
    let data = [...dataTable];
    
    // 1. Filtro
    const search = searchField.toLowerCase().replace(/\s/g, '');
    if (search) {
      data = data.filter(emb => {
        const nome = (emb.nome || '').toLowerCase();
        const grupo = (emb.grupos?.[0]?.nome || '').toLowerCase();
        return (nome + grupo).replace(/\s/g, '').includes(search);
      });
    }

    // 2. Ordenação
    data.sort((a, b) => {
      const aVal = orderBy === 'grupo' ? (a.grupos?.[0]?.nome || '') : (a.nome || '');
      const bVal = orderBy === 'grupo' ? (b.grupos?.[0]?.nome || '') : (b.nome || '');
      return aVal.localeCompare(bVal) * (orderDir === 'asc' ? 1 : -1);
    });

    return data;
  }, [dataTable, searchField, orderBy, orderDir]);

  const sortLabelSx = {
    color: '#fff',
    '&.Mui-active': { color: '#fff' },
    '&:hover': { color: '#fff' },
    '& .MuiTableSortLabel-icon': { color: '#fff !important' }
  };

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      {permission === 'w' && (
        <>
          <D className="row w aF">
            <SearchField
              value={searchField}
              onChange={v => setSearchField(v)}
            />
            <D e /><D size="2rem" />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => openEditDialog(null, false)}
            >
              <Add /> Embarcação
            </Button>
          </D>
          <D h="1rem" />
        </>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow sx={{ height: '3rem', background: '#ccc' }}>
                {permission === 'w' && <TCell align="center" width={ICON_CELL_WIDTH}>Editar</TCell>}
                {permission === 'w' && <TCell align="center" width={ICON_CELL_WIDTH}>Excluir</TCell>}

                <TCell sx={{ width: '28ch' }}>
                  <TableSortLabel
                    active={orderBy === 'nome'}
                    direction={orderBy === 'nome' ? orderDir : 'asc'}
                    onClick={() => handleSort('nome')}
                    sx={sortLabelSx}
                  >
                    Nome ({dataTable.length} embarcações)
                  </TableSortLabel>
                </TCell>

                <TCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'grupo'}
                    direction={orderBy === 'grupo' ? orderDir : 'asc'}
                    onClick={() => handleSort('grupo')}
                    sx={sortLabelSx}
                  >
                    Filial-Grupo
                  </TableSortLabel>
                </TCell>
              </TRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TRow>
                  <TCell component="th" align="center" colSpan={4}>
                    <Loading />
                  </TCell>
                </TRow>
              ) : processedData.length > 0 ? (
                processedData.map(emb => (
                  <TRow key={emb.id}>
                    {permission === 'w' && (
                      <TCell align="center" width={ICON_CELL_WIDTH}>
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openEditDialog(emb, true)}
                        >
                          <Edit />
                        </IconButton>
                      </TCell>
                    )}
                    
                    {permission === 'w' && (
                      <TCell align="center" width={ICON_CELL_WIDTH}>
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openDeleteDialog(emb)}
                        >
                          <Delete />
                        </IconButton>
                      </TCell>
                    )}
                    
                    <TCell sx={{ width: '25ch' }}>{emb.nome}</TCell>
                    <TCell sx={{ whiteSpace: 'nowrap' }}>{emb.grupos?.[0]?.nome || ''}</TCell>
                  </TRow>
                ))
              ) : (
                <TRow>
                  <TCell align="center" colSpan={4}>
                    Nenhuma embarcação cadastrada
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
}